/**
 * §13 — Panelin tek biçimlendirme katmanı. Tüm sayı/para/tarih metinleri tr-TR'dir:
 * binlik ayracı '.', ondalık ayracı ',', para birimi son ekte ('45.320 ₺').
 *
 * Zaman dilimi Europe/Istanbul'a SABİTLENİR: sunucu epoch/ISO-UTC üretir (§8.1),
 * panel Türkiye saatiyle okunur; makine TZ'si testleri kaydırmasın.
 */

/** Ürünün tek yerel ayarı — §13 arayüzü Türkçedir. */
const LOCALE = 'tr-TR';
/** §13 — panel Türkiye saatine göre okunur. */
const TIME_ZONE = 'Europe/Istanbul';

/** Türk Lirası sembolü — kod içinde tek kaynak. */
export const TL = '₺';

function nf(value: number, maxDigits: number, minDigits = 0): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value);
}

/** Sonlu olmayan/eksik girdileri 0'a düşürür — panelde asla "NaN" görünmez. */
function safe(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Ham sayı — '45.320' (ondalıksız, tr-TR gruplama). */
export function formatNumber(value: number | null | undefined, digits = 0): string {
  return nf(safe(value), digits);
}

/** §12.2 para gösterimi — '45.320 ₺' (ondalıksız; kuruş panelde gösterilmez). */
export function formatTL(value: number | null | undefined): string {
  return `${nf(safe(value), 0)} ${TL}`;
}

/**
 * Kart/rozet gibi dar alanlar için kısaltılmış para — '45 bin ₺', '1,2 mn ₺'.
 * 1000'in altı tam gösterilir ('950 ₺').
 */
export function formatTLShort(value: number | null | undefined): string {
  const n = safe(value);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${nf(n / 1_000_000, 1)} mn ${TL}`;
  if (abs >= 1_000) return `${nf(n / 1_000, 0)} bin ${TL}`;
  return formatTL(n);
}

/**
 * Yüzde PUANI biçimler — girdi zaten yüzdedir (ör. `FunnelStepMetrics.drop_pct` = 42.7).
 * '%42,7'. Oran (0..1) elinizdeyse `formatRatioPct` kullanın.
 */
export function formatPct(value: number | null | undefined, digits = 1): string {
  return `%${nf(safe(value), digits, digits)}`;
}

/** Oranı (0..1) yüzdeye çevirip biçimler — ör. `OverviewResponse.cr` = 0.0234 → '%2,3'. */
export function formatRatioPct(value: number | null | undefined, digits = 1): string {
  return formatPct(safe(value) * 100, digits);
}

/** ISO-8601 / epoch ms / Date kabul eder; geçersizse null. */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Tarih — '03.08.2026'. Geçersiz girdi '—' döner (boş hücre işareti). */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** Tarih + saat — '03.08.2026 14:00' (oturum zaman çizelgesi §13.5). */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Saat:dakika:saniye — olay zaman çizelgesinde satır başı (§13.5). */
export function formatTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
}

/**
 * §103.4 — ISO hafta ARALIĞI: '11 – 17 Ağustos'.
 *
 * Girdi kohort haftasının Pazartesi'sidir (`RetentionWeek.start`, `YYYY-MM-DD`).
 * Hesap UTC'dedir çünkü hafta sınırı UTC'de tanımlıdır (`packages/core/retention.ts`);
 * biçimlemede yerel saate geçmek Pazartesi'yi Pazar gösterebilirdi.
 *
 * Ay ya da yıl DEĞİŞİYORSA iki uçta da yazılır — yılı atlayan bir aralık
 * ('29 Aralık – 4 Ocak') okuyucuya hangi yıla ait olduğunu söylemez.
 */
export function formatWeekRange(
  mondayIsoDay: string | null | undefined,
  /** Kaç hafta İLERİ kaydırılacağı — retention'ın `+k` hücreleri için. */
  weekOffset = 0,
): string {
  if (typeof mondayIsoDay !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(mondayIsoDay)) return '—';
  const kok = new Date(`${mondayIsoDay}T00:00:00.000Z`);
  if (Number.isNaN(kok.getTime())) return '—';
  const bas = new Date(kok.getTime() + weekOffset * 7 * 86_400_000);
  const son = new Date(bas.getTime() + 6 * 86_400_000);

  const gun = (d: Date): string => String(d.getUTCDate());
  const ay = (d: Date): string =>
    new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', month: 'long' }).format(d);
  const yil = (d: Date): number => d.getUTCFullYear();

  if (yil(bas) !== yil(son)) {
    return `${gun(bas)} ${ay(bas)} ${yil(bas)} – ${gun(son)} ${ay(son)} ${yil(son)}`;
  }
  if (bas.getUTCMonth() !== son.getUTCMonth()) {
    return `${gun(bas)} ${ay(bas)} – ${gun(son)} ${ay(son)}`;
  }
  return `${gun(bas)} – ${gun(son)} ${ay(son)}`;
}

/** §103.4 — ay anahtarı ('2026-08') → 'Ağustos 2026'. Geçersiz girdi '—'. */
export function formatMonthKey(key: string | null | undefined): string {
  if (typeof key !== 'string' || !/^\d{4}-\d{2}$/.test(key)) return '—';
  const d = new Date(`${key}-01T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(d);
}

/** Süre — '2 dk 05 sn' / '48 sn' (oturum listesi `duration_ms`). */
export function formatDuration(ms: number | null | undefined): string {
  const total = Math.max(0, Math.round(safe(ms) / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  if (min === 0) return `${sec} sn`;
  return `${min} dk ${String(sec).padStart(2, '0')} sn`;
}

/**
 * Geçen süre — '12 saniye önce' / '3 dakika önce' / '2 saat önce' / '4 gün önce'.
 *
 * §13 kurulum durumu için: "son olay 13.08.2026 10:42" cümlesi kullanıcıya SAAT
 * ARİTMETİĞİ yaptırır; oysa sorduğu soru "AKIYOR MU"dur ve cevabı tazeliktir.
 * Gelecek damgalar (istemci saati geride) 'az önce' sayılır — negatif süre yazmak
 * veriyi değil, saat farkını anlatırdı.
 */
export function formatAgo(
  value: string | number | Date | null | undefined,
  now: number = Date.now(),
): string {
  const d = toDate(value);
  if (!d) return '—';
  const diff = now - d.getTime();
  if (diff < 10_000) return 'az önce';
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec} saniye önce`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} dakika önce`;
  const hour = Math.round(min / 60);
  if (hour < 24) return `${hour} saat önce`;
  return `${Math.round(hour / 24)} gün önce`;
}

/**
 * §12.2 — "Etki her zaman ARALIK olarak gösterilir": 'tahmini 45-135 bin ₺/ay'.
 * Ölçek (bin / mn) aralığın üst ucuna göre seçilir ve son ekte BİR KEZ yazılır;
 * `insight-engine/formatImpactRangeTr` her iki ucu ayrı ayrı etiketler — panelde
 * §13'ün istediği kısa biçim kullanılır.
 *
 * Etki hesaplanmayan bilgi kuralları (R19 gibi, impact = null) → 'etki hesaplanmaz'.
 */
export function formatImpactRange(
  low: number | null | undefined,
  high: number | null | undefined,
): string {
  if (low === null || low === undefined || high === null || high === undefined) {
    return 'etki hesaplanmaz';
  }
  const lo = safe(low);
  const hi = safe(high);
  const scale = Math.max(Math.abs(lo), Math.abs(hi));

  if (scale >= 1_000_000) {
    return `tahmini ${nf(lo / 1_000_000, 1)}-${nf(hi / 1_000_000, 1)} mn ${TL}/ay`;
  }
  if (scale >= 1_000) {
    return `tahmini ${nf(lo / 1_000, 0)}-${nf(hi / 1_000, 0)} bin ${TL}/ay`;
  }
  return `tahmini ${nf(lo, 0)}-${nf(hi, 0)} ${TL}/ay`;
}

/** Uzun URL/desenleri tabloda tek satıra sığdırmak için ortadan kırpar. */
export function truncateMiddle(text: string, max = 48): string {
  if (text.length <= max) return text;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${text.slice(0, head)}…${text.slice(text.length - tail)}`;
}

/** 'YYYY-MM-DD' — API'nin `from`/`to` parametre biçimi (§10 overview). */
export function toIsoDay(value: string | number | Date): string {
  const d = toDate(value) ?? new Date();
  return d.toISOString().slice(0, 10);
}
